import React, { useRef, useEffect, useState } from "react";
import Hero from "./Hero";
import Navbar from "./NavBar";
import TradeSection from "./TradeSection";
import ExpertiseSection from "./ExpertiseSection";
import RiskCapitalSection from "./RiskCapitalSection";
import CapabilityAreasSection from "./CapabilityAreasSection";
import FeaturedInsights from "./FeaturedInsights";
import KeepExploring from "./KeepExploring";
import IndustryExperience from "./IndustryExperience";
import OurStory from "./OurStory";
import ContactBanner from "./ContactBanner";
import Footer from "./Footer";
import Capabilities from "./Capabilities";
import Industries from "./industries";
import Insights from "./Insights";
import AboutUs from "./AboutUs";
import Search1 from "./Search";
import content from "../content/content.json";

const sections = [
  "ExpertiseSection",
  "RiskCapitalSection",
  "CapabilityAreasSection",
  "FeaturedInsights",
  "KeepExploring",
  "IndustryExperience",
  "OurStory",
  "ContactBanner",
  "Footer",
];

const Main = () => {
  // ── Refs & state ───────────────────────────────────────────────
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const sectionRefs = useRef(sections.map(() => React.createRef()));
  const [visibleSections, setVisibleSections] = useState(new Array(sections.length).fill(false));
  const [currentPanel, setCurrentPanel] = useState(0);
  const panelWidthRef = useRef(window.innerWidth);
  const currentPanelRef = useRef(0);
  const isScrollingRef = useRef(false);
  const sliderLockedRef = useRef(true);

  // ── Derived constants ──────────────────────────────────────────
  const { main, tradeSection } = content;
  const panelCount = 1 + tradeSection.slides.length;
  const containerHeightVh = 100;

  // ── Skip: jump to last slide, release lock, scroll down ────────
  const scrollToNextSection = () => {
    const lastPanel = panelCount - 1;
    const nextSectionTop = containerRef.current
      ? containerRef.current.offsetTop + containerRef.current.offsetHeight
      : sectionRefs.current[0]?.current?.offsetTop || window.innerHeight;

    sliderLockedRef.current = false;
    isScrollingRef.current = false;
    currentPanelRef.current = lastPanel;
    setCurrentPanel(lastPanel);

    if (innerRef.current) {
      innerRef.current.style.transform = `translateX(-${lastPanel * panelWidthRef.current}px)`;
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: nextSectionTop,
        behavior: "smooth",
      });
    });
  };

  // ── Intersection observer: fade-in sections below slider ───────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            const index = sectionRefs.current.findIndex((ref) => ref.current === target);
            if (index !== -1 && !visibleSections[index]) {
              setVisibleSections((prev) => {
                const newVis = [...prev];
                newVis[index] = true;
                return newVis;
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, [visibleSections]);

  // ── Keep transform in sync on resize ──────────────────────────
  useEffect(() => {
    const updateSizes = () => {
      panelWidthRef.current = window.innerWidth;
      if (innerRef.current) {
        innerRef.current.style.transform = `translateX(-${currentPanelRef.current * panelWidthRef.current}px)`;
      }
    };
    updateSizes();
    window.addEventListener("resize", updateSizes);
    return () => window.removeEventListener("resize", updateSizes);
  }, []);

  // ── Wheel/keyboard hard lock ───────────────────────────────────
  useEffect(() => {
    const TRANSITION_MS = 600;
    const SCROLL_THRESHOLD = 30;

    const goToPanel = (next) => {
      if (isScrollingRef.current) return;
      isScrollingRef.current = true;
      currentPanelRef.current = next;
      setCurrentPanel(next);
      if (innerRef.current) {
        innerRef.current.style.transform = `translateX(-${next * panelWidthRef.current}px)`;
      }
      setTimeout(() => { isScrollingRef.current = false; }, TRANSITION_MS);
    };

    const onWheel = (e) => {
      if (!sliderLockedRef.current) return;
      e.preventDefault();
      if (isScrollingRef.current) return;

      if (e.deltaY > SCROLL_THRESHOLD) {
        if (currentPanelRef.current < panelCount - 1) {
          goToPanel(currentPanelRef.current + 1);
        } else {
          sliderLockedRef.current = false;
        }
      } else if (e.deltaY < -SCROLL_THRESHOLD) {
        if (currentPanelRef.current > 0) {
          goToPanel(currentPanelRef.current - 1);
        }
      }
    };

    const onKeyDown = (e) => {
      if (!sliderLockedRef.current) return;
      let next = currentPanelRef.current;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") next = Math.min(next + 1, panelCount - 1);
      else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = Math.max(next - 1, 0);
      else return;
      e.preventDefault();
      if (next !== currentPanelRef.current) goToPanel(next);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [panelCount]);

  // ── Re-lock and reset when user scrolls back to the top ────────
  useEffect(() => {
    const onScroll = () => {
      if (sliderLockedRef.current) return;
      if (window.scrollY <= 0) {
        sliderLockedRef.current = true;
        currentPanelRef.current = 0;
        setCurrentPanel(0);
        if (innerRef.current) innerRef.current.style.transform = "translateX(0px)";
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="overflow-x-hidden">
      <Navbar
        capabilities={<Capabilities />}
        industries={<Industries />}
        insights={<Insights />}
        about={<AboutUs />}
      />

      <section ref={containerRef} className="relative" style={{ height: `${containerHeightVh}vh` }}>
        <div className="md:hidden h-screen w-screen">
          <Hero onScrollClick={scrollToNextSection} />
        </div>

        <div className="hidden md:block sticky top-0 h-screen w-screen overflow-hidden">
          <div ref={innerRef} className="inner flex transition-transform duration-[500ms] ease-in-out h-full">
            <div className="w-screen h-full shrink-0 bg-[#1c1f2b]">
              <Hero onScrollClick={scrollToNextSection} />
            </div>

            {tradeSection.slides.map((_, i) => (
              <div key={i} className="w-screen h-full shrink-0 bg-black flex items-center justify-center text-white">
                <TradeSection activeIndex={i} onScrollClick={scrollToNextSection} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[0] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[0]}
      >
        <ExpertiseSection />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[1] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[1]}
      >
        <RiskCapitalSection />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[2] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[2]}
      >
        <CapabilityAreasSection />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[3] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[3]}
      >
        <FeaturedInsights />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[4] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[4]}
      >
        <KeepExploring />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[5] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[5]}
      >
        <IndustryExperience />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[6] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[6]}
      >
        <OurStory />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[7] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[7]}
      >
        <ContactBanner />
      </section>

      <section
        className={`flex items-center justify-center py-0 transition-opacity duration-1000 ease-out transform ${
          visibleSections[8] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
        ref={sectionRefs.current[8]}
      >
        <Footer />
      </section>

      {/* <div className="fixed bottom-4 left-4 bg-black/80 text-white p-3 rounded-lg z-50 text-xs space-y-1">
        <div>
          {main.debugPanel}: {currentPanel + 1} / {panelCount}
        </div>
        <div>
          {main.debugWidth}: {panelWidthRef.current}px
        </div>
        <div>
          {main.debugTranslate}: -{currentPanel * panelWidthRef.current}px
        </div>
      </div> */}
    </div>
  );
};

export default Main;
